/**
 * Recorder playback inside one segment (docs/mobile-editor-layout-spec.md
 * 11.1). Absolute times are the source video's; relative times run from 0 to
 * the segment's length, which is what the phone timeline shows. Pure, so the
 * rules are unit tested.
 */
export interface SegRange {
  /** Absolute start, seconds. */
  start: number;
  /** Absolute end, seconds; Infinity while the video's length is unknown. */
  end: number;
}

/** A hair under the end counts as the end: iOS reads currentTime back a little low. */
export const END_EPSILON = 0.05;
/** How far before the start playback may drift before it is put back. */
export const START_EPSILON = 0.05;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The segment's length, or 0 while it is unknown. */
export function segLength(r: SegRange): number {
  return Number.isFinite(r.end) ? Math.max(0, r.end - r.start) : 0;
}

/** Absolute time to the timeline's 0..length. */
export function toRel(t: number, r: SegRange): number {
  return clamp(t - r.start, 0, segLength(r));
}

/** The timeline's 0..length back to absolute time, never outside the segment. */
export function toAbs(rel: number, r: SegRange): number {
  return r.start + clamp(rel, 0, segLength(r));
}

/**
 * Where a press of play starts from: the start when the video sits before
 * the segment or at (or past) its end, otherwise where it is (null).
 */
export function playFrom(t: number, r: SegRange): number | null {
  if (t < r.start - START_EPSILON) return r.start;
  if (Number.isFinite(r.end) && t >= r.end - END_EPSILON) return r.start;
  return null;
}

/** Playback that has left the segment: before it, past its end, or neither. */
export function outOfRange(t: number, r: SegRange): 'before' | 'after' | null {
  if (t < r.start - START_EPSILON) return 'before';
  if (Number.isFinite(r.end) && t >= r.end - END_EPSILON) return 'after';
  return null;
}

/**
 * Parking at the end: the time to seek to, or null when the video is already
 * on the end frame. Seeking only when it overshot by more than a frame keeps
 * a parked video from seeking again on every timeupdate.
 */
export function parkAt(t: number, r: SegRange): number | null {
  return Math.abs(t - r.end) > 1 / 30 ? r.end : null;
}

function mmss(s: number): string {
  const v = Math.max(0, Math.round(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;
}

/**
 * The readout beside the timeline, "0:01 / 0:03", from the same relative time
 * the thumb shows. Both are rounded the same way, so the two never disagree
 * and the end reads as the length (not "0:02 / 0:03").
 */
export function readout(rel: number, length: number): string {
  return `${mmss(Math.min(rel, length))} / ${mmss(length)}`;
}
